<template>
  <div>
    <Header2 />

    <div class="rg-c">
      <div class="gc-x">
        <div class="header-section">
          <div class="otp-icon">🔐</div>
          <h2 class="hc-x">OTP Verification</h2>
          <p class="subtitle">Enter the 4-digit code sent to your email</p>
        </div>

        <main>
          <Message :status="status" :message="message" />

          <form @submit.prevent="handleSubmit">
            <div class="otp-container">
              <input
                v-for="(digit, index) in otpDigits"
                :key="index"
                :ref="`otp${index}`"
                type="text"
                inputmode="numeric"
                maxlength="1"
                class="otp-box"
                :class="{ filled: digit }"
                v-model="otpDigits[index]"
                @input="handleInput(index)"
                @keydown="handleKeydown($event, index)"
                @paste="handlePaste"
                autocomplete="one-time-code"
              />
            </div>

            <div class="ml-xf">
              <button :disabled="isDisabled || !isComplete">
                {{ btnText }}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import Header2 from "../../components/header2.vue";
import Message from "../../components/message.vue";
import axios from "axios";

export default {
  name: "VerifyOtp",
  components: { Header2, Message },
  data() {
    return {
      otpDigits: ["", "", "", ""],
      status: null,
      message: "",
      btnText: "Verify OTP",
      isDisabled: false,
      resendCountdown: 60,
      countdownTimer: null,
    };
  },

  computed: {
    isComplete() {
      return this.otpDigits.every((d) => d !== "");
    },
    code() {
      return this.otpDigits.join("");
    },
    adminId() {
      const data = JSON.parse(localStorage.getItem("id"));
      return data || "";
    },
  },
  methods: {
    handleInput(index) {
      const val = this.otpDigits[index];
      // allow only numbers
      if (!/^\d$/.test(val)) {
        this.otpDigits[index] = "";
        return;
      }
      // move to next box
      if (val && index < 5) {
        this.$nextTick(() => {
          this.$refs[`otp${index + 1}`][0].focus();
        });
      }
    },
    handleKeydown(e, index) {
      if (e.key === "Backspace") {
        if (!this.otpDigits[index] && index > 0) {
          this.otpDigits[index - 1] = "";
          this.$nextTick(() => {
            this.$refs[`otp${index - 1}`][0].focus();
          });
        }
      }
    },
    handlePaste(e) {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);
      pasted.split("").forEach((char, i) => {
        this.otpDigits[i] = char;
      });
      this.$nextTick(() => {
        const nextEmpty = this.otpDigits.findIndex((d) => d === "");
        const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
        this.$refs[`otp${focusIndex}`][0].focus();
      });
    },
    startCountdown() {
      this.resendCountdown = 60;
      this.countdownTimer = setInterval(() => {
        if (this.resendCountdown > 0) {
          this.resendCountdown--;
        } else {
          clearInterval(this.countdownTimer);
        }
      }, 1000);
    },

    async handleSubmit() {
      if (!this.isComplete) return;
      this.btnText = "Verifying...";
      this.isDisabled = true;

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}api/auth/admin/verify-login`,
          { id: this.adminId, code: this.code },
        );

        localStorage.setItem("admin", JSON.stringify(response.data));

        this.$router.push("/admin/dashboard");
      } catch (e) {
        const status = e.response?.status;
        this.status = false;
        this.message =
          status == 400 || status == 422
            ? e.response.data.message
            : "Connection problem, try checking your network";
        this.btnText = "Verify OTP";
        this.isDisabled = false;
        this.otpDigits = ["", "", "", ""];
        this.$nextTick(() => this.$refs["otp0"][0].focus());
        setTimeout(() => (this.status = null), 3000);
      }
    },
  },

  beforeUnmount() {
    clearInterval(this.countdownTimer);
  },
};
</script>

<style scoped>
.rg-c {
  font-family: "Quicksand", Georgia, "Times New Roman", Times, serif;
  font-size: 1.6rem;
  padding: 10px;
}

.rg-c .gc-x {
  max-width: 420px;
  width: 100%;
  margin: 100px auto;
  background: #fff;
  box-shadow: 0px 4px 24px rgba(62, 57, 107, 0.12);
  padding: 30px 20px;
  border-radius: 20px;
}

.header-section {
  text-align: center;
  margin-bottom: 20px;
}

.otp-icon {
  font-size: 2.5rem;
  margin-bottom: 8px;
}

.hc-x {
  font-size: 1.3rem;
  margin: 0 0 6px;
  color: #222;
  font-weight: 700;
}

.subtitle {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

main {
  margin-top: 20px;
}

/* OTP Boxes */
.otp-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 24px 0;
}

.otp-box {
  width: 48px !important;
  height: 56px;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0a1aa8;
  border: 2px solid #ddd;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.1s;
  background: #f9f9ff;
  padding: 0;
  caret-color: #0a1aa8;
}

.otp-box:focus {
  border-color: #0a1aa8;
  box-shadow: 0 0 0 3px rgba(10, 26, 168, 0.12);
  transform: scale(1.05);
  background: #fff;
}

.otp-box.filled {
  border-color: #0a1aa8;
  background: #eef0fd;
}

/* Submit Button */
.ml-xf {
  width: 100%;
  padding: 0 10px;
}

button[type="submit"],
button:not(.resend-btn) {
  padding: 14px;
  color: #fff;
  font-size: 1rem;
  font-family: "Quicksand", Georgia, "Times New Roman", Times, serif;
  font-weight: 600;
  background: #0a1aa8;
  border: none;
  border-radius: 12px;
  width: 100%;
  cursor: pointer;
  box-shadow: 0px 4px 12px rgba(10, 26, 168, 0.25);
  transition: background 0.2s, opacity 0.2s;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):hover {
  background: #0b20c4;
}

/* Resend Section */
.resend-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
}

.resend-text {
  font-size: 0.85rem;
  color: #888;
}

.resend-btn {
  background: none !important;
  border: none !important;
  color: #0a1aa8 !important;
  font-size: 0.9rem !important;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 0 !important;
  box-shadow: none !important;
  width: auto !important;
  text-decoration: underline;
  font-family: "Quicksand", Georgia, "Times New Roman", Times, serif;
}

.resend-btn:disabled {
  color: #aaa !important;
  cursor: not-allowed;
  text-decoration: none;
}

@media screen and (max-width: 490px) {
  .rg-c .gc-x {
    margin-top: 80px;
    border-radius: 20px;
  }

  .otp-box {
    width: 42px !important;
    height: 50px;
    font-size: 1.2rem;
    border-radius: 10px;
  }
}
</style>
